import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./QueuePage.css";

const API = process.env.REACT_APP_API_BASE
  || "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

export default function QueuePage({ brandName = "QKARD", accentColor = "#6c63ff" }) {
  const { queueId } = useParams();

  const [queue, setQueue] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [cart, setCart] = useState({});
  const [phone, setPhone] = useState("");
  const [joining, setJoining] = useState(false);
  const [gpsError, setGpsError] = useState("");

  const storageKey = `syncfield_queue_token_${queueId}`;
  const [myToken, setMyToken] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)); } catch { return null; }
  });

  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API}/queue/${queueId}`);
      if (res.status === 404) { setNotFound(true); setLoading(false); return; }
      const data = await res.json();
      setQueue(data.queue || null);
      setTokens(data.tokens || []);
      setLoading(false);
    } catch {
      setError("Could not load queue. Check your connection.");
      setLoading(false);
    }
  }, [queueId]);

  useEffect(() => {
    fetchQueue();
    const iv = setInterval(fetchQueue, 8000);
    return () => clearInterval(iv);
  }, [fetchQueue]);

  useEffect(() => {
    if (!myToken || !tokens.length) return;
    const live = tokens.find(t => t.tokenId === myToken.tokenId);
    if (live && live.status !== myToken.status) {
      const updated = { ...myToken, status: live.status };
      setMyToken(updated);
      localStorage.setItem(storageKey, JSON.stringify(updated));
    }
    if (live && (live.status === "billed" || live.status === "done")) {
      const joinedAt = myToken.joinedAt || new Date().toISOString();
      const age = Date.now() - new Date(joinedAt).getTime();
      if (age > 30000) {
        setTimeout(() => { localStorage.removeItem(storageKey); setMyToken(null); }, 6000);
      }
    }
  }, [tokens, myToken, storageKey]);

  useEffect(() => {
    if (!queue?.catalogueSlug) return;
    setItemsLoading(true);
    fetch(`${API}/catalogue/${queue.catalogueSlug}`)
      .then(r => r.json())
      .then(d => setItems((d.items || []).filter(i => i.inStock !== false && i.outOfStock !== true)))
      .catch(() => {})
      .finally(() => setItemsLoading(false));
  }, [queue?.catalogueSlug]);

  const cartItems = items.filter(i => (cart[i.itemId] || 0) > 0).map(i => ({ ...i, qty: cart[i.itemId] }));
  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);
  const addItem = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const removeItem = (id) => setCart(c => ({ ...c, [id]: Math.max(0, (c[id] || 0) - 1) }));

  const handleGetToken = async () => {
    setGpsError("");
    if (cartCount === 0) { setGpsError("Please select at least one item."); return; }
    setJoining(true);
    let gpsLat = null, gpsLng = null;
    if (queue.geofence && !queue.testMode) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 })
        );
        gpsLat = pos.coords.latitude;
        gpsLng = pos.coords.longitude;
      } catch {
        setGpsError("Location access denied. Please allow location to join the queue.");
        setJoining(false);
        return;
      }
    }
    try {
      const res = await fetch(`${API}/queue/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          queueId,
          customerName: phone ? "Customer" : "Guest",
          customerPhone: phone || null,
          items: cartItems.map(i => ({ itemId: i.itemId, name: i.name, price: i.price, qty: i.qty })),
          gpsLat, gpsLng,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to join");
      const token = { tokenId: data.tokenId, tokenNumber: data.tokenNumber, status: "waiting", items: cartItems, total: cartTotal, joinedAt: new Date().toISOString() };
      setMyToken(token);
      localStorage.setItem(storageKey, JSON.stringify(token));
      await fetchQueue();
    } catch (e) {
      setGpsError(e.message || "Could not join queue");
    } finally {
      setJoining(false);
    }
  };

  const waitingTokens = tokens.filter(t => t.status === "waiting");
  const currentToken = tokens.find(t => t.status === "calling" || t.status === "serving");
  let myPosition = null;
  if (myToken) {
    const idx = waitingTokens.findIndex(t => t.tokenId === myToken.tokenId);
    myPosition = idx >= 0 ? idx + 1 : null;
  }

  const getMyMessage = () => {
    if (!myToken) return { emoji: "🎫", text: "You're in the queue!", cls: "msg-waiting" };
    const live = tokens.find(t => t.tokenId === myToken.tokenId);
    const status = live?.status || myToken.status;
    if (status === "calling" || status === "serving") return { emoji: "🔔", text: "It's your turn! Please come forward.", cls: "msg-now" };
    if (status === "done" || status === "billed") return { emoji: "✅", text: "You're all done! Thanks.", cls: "msg-done" };
    if (myPosition === 1) return { emoji: "⏳", text: "You're next! Get ready.", cls: "msg-next" };
    if (myPosition !== null) return { emoji: "🪑", text: `${myPosition - 1} ${myPosition - 1 === 1 ? "person" : "people"} ahead of you`, cls: "msg-waiting" };
    return { emoji: "🎫", text: "You're in the queue!", cls: "msg-waiting" };
  };
  const myMsg = getMyMessage();

  const grouped = items.reduce((acc, item) => {
    const cat = item.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (loading) return (
    <div className="qp-loading">
      <div className="qp-spinner" style={{ borderTopColor: accentColor }} />
      <p>Loading…</p>
    </div>
  );

  if (notFound) return (
    <div className="qp-not-found">
      <div className="qp-nf-icon">🔍</div>
      <h2>Queue not found</h2>
      <p>This queue link may have expired.</p>
    </div>
  );

  if (myToken) return (
    <div className="qp-root">
      <div className="qp-header" style={{ borderBottom: `3px solid ${accentColor}` }}>
        <div className="qp-logo" style={{ color: accentColor }}>{brandName}</div>
        <h1 className="qp-biz-name">{queue?.businessName || "Queue"}</h1>
      </div>
      <div className="qp-counter-section">
        <div className="qp-counter-card">
          <span className="qp-counter-label">Now Serving</span>
          <span className="qp-counter-num" style={{ color: accentColor }}>{currentToken ? `#${currentToken.tokenNumber}` : "—"}</span>
        </div>
        <div className="qp-counter-card">
          <span className="qp-counter-label">Waiting</span>
          <span className="qp-counter-num" style={{ color: accentColor }}>{waitingTokens.length}</span>
        </div>
      </div>
      {myMsg.cls === "msg-now" && (
        <div style={{ background: "#22c55e", padding: "16px 20px", textAlign: "center", fontSize: 18, fontWeight: 700, color: "#000" }}>
          YOUR TURN - Please come to the counter!
        </div>
      )}
      <div className={`qp-my-token ${myMsg.cls}`}>
        <div className="qp-my-token-num" style={{ color: accentColor }}>#{String(myToken.tokenNumber).padStart(3, "0")}</div>
        <div className="qp-my-token-msg">{myMsg.emoji} {myMsg.text}</div>
        {myPosition > 1 && <div className="qp-my-token-sub">~{(myPosition - 1) * 5} min estimated wait</div>}
        {myToken.items?.length > 0 && (
          <div style={{ marginTop: 16, textAlign: "left", background: "rgba(255,255,255,0.05)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, letterSpacing: 1, color: "#aaa", marginBottom: 8, fontWeight: 700 }}>YOUR ORDER</div>
            {myToken.items.map(i => (
              <div key={i.itemId} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#ddd", padding: "3px 0" }}>
                <span>{i.name} x{i.qty}</span><span>Rs.{i.price * i.qty}</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid #333", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#fff" }}>
              <span>Total</span><span>Rs.{myToken.total}</span>
            </div>
          </div>
        )}
      </div>
      <div className="qp-footer">Powered by <strong style={{ color: accentColor }}>{brandName}</strong></div>
    </div>
  );

  return (
    <div className="qp-root">
      <div className="qp-header" style={{ borderBottom: `3px solid ${accentColor}` }}>
        <div className="qp-logo" style={{ color: accentColor }}>{brandName}</div>
        <h1 className="qp-biz-name">{queue?.businessName || "Queue"}</h1>
        {queue?.queueLabel && <p className="qp-label">{queue.queueLabel}</p>}
        {queue?.isPaused && <div className="qp-paused-badge">Queue Paused</div>}
        {queue?.testMode && (
          <div style={{ marginTop: 8, background: "#2a2000", border: "1px solid #f5a623", borderRadius: 8, padding: "4px 12px", fontSize: 12, color: "#f5a623", fontWeight: 600, display: "inline-block" }}>
            Test Mode - GPS bypassed
          </div>
        )}
      </div>

      <div className="qp-counter-section">
        <div className="qp-counter-card">
          <span className="qp-counter-label">Now Serving</span>
          <span className="qp-counter-num" style={{ color: accentColor }}>{currentToken ? `#${currentToken.tokenNumber}` : "—"}</span>
        </div>
        <div className="qp-counter-card">
          <span className="qp-counter-label">Waiting</span>
          <span className="qp-counter-num" style={{ color: accentColor }}>{waitingTokens.length}</span>
        </div>
      </div>

      {queue?.isPaused ? (
        <div className="qp-join-section"><div className="qp-paused-msg">Queue is currently paused. Please check back soon.</div></div>
      ) : (
        <>
          <div style={{ padding: "0 16px 100px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#aaa", letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Select Items</div>
            {itemsLoading && <div style={{ color: "#666", textAlign: "center", padding: 20 }}>Loading items…</div>}
            {Object.entries(grouped).map(([cat, catItems]) => (
              <div key={cat} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#555", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>{cat}</div>
                {catItems.map(item => (
                  <div key={item.itemId} style={{ background: "#1a1a1a", border: (cart[item.itemId] || 0) > 0 ? `1px solid ${accentColor}` : "1px solid #2a2a2a", borderRadius: 12, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                    {item.photoUrl && <img src={item.photoUrl} alt={item.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 2 }}>{item.name}</div>
                      {item.description && <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{item.description}</div>}
                      <div style={{ fontSize: 16, fontWeight: 700, color: accentColor }}>Rs.{item.price}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      {(cart[item.itemId] || 0) > 0 ? (
                        <>
                          <button onClick={() => removeItem(item.itemId)} style={{ width: 32, height: 32, background: "#333", color: "#fff", border: "none", borderRadius: 8, fontSize: 18, fontWeight: 700, cursor: "pointer" }}>-</button>
                          <span style={{ fontSize: 16, fontWeight: 700, color: "#fff", minWidth: 20, textAlign: "center" }}>{cart[item.itemId]}</span>
                          <button onClick={() => addItem(item.itemId)} style={{ width: 32, height: 32, background: accentColor, color: "#fff", border: "none", borderRadius: 8, fontSize: 18, fontWeight: 700, cursor: "pointer" }}>+</button>
                        </>
                      ) : (
                        <button onClick={() => addItem(item.itemId)} style={{ background: accentColor, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ ADD</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {items.length === 0 && !itemsLoading && <div style={{ color: "#555", textAlign: "center", padding: 20, fontSize: 14 }}>No items available</div>}
          </div>

          {cartCount > 0 && (
            <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#0f0f0f", borderTop: "1px solid #222", padding: "14px 16px" }}>
              <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "10px 14px", marginBottom: 10 }}>
                {cartItems.map(i => (
                  <div key={i.itemId} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#ccc", padding: "2px 0" }}>
                    <span>{i.name} x{i.qty}</span><span>Rs.{i.price * i.qty}</span>
                  </div>
                ))}
                <div style={{ borderTop: "1px solid #333", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#fff", fontSize: 15 }}>
                  <span>Total</span><span>Rs.{cartTotal}</span>
                </div>
              </div>
              <input type="tel" placeholder="Phone number (optional)" value={phone} onChange={e => setPhone(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: "#111", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", fontSize: 15, color: "#fff", marginBottom: 8, outline: "none", fontFamily: "inherit" }} />
              {gpsError && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 8, textAlign: "center" }}>{gpsError}</div>}
              {queue?.geofence && !queue?.testMode && (
                <div style={{ fontSize: 12, color: "#666", textAlign: "center", marginBottom: 8 }}>Location will be verified — you must be at {queue.businessName}</div>
              )}
              <button onClick={handleGetToken} disabled={joining}
                style={{ width: "100%", background: joining ? "#333" : accentColor, color: "#fff", border: "none", borderRadius: 12, padding: 16, fontSize: 17, fontWeight: 700, cursor: joining ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {joining ? "Getting your token..." : `Get Token - Rs.${cartTotal}`}
              </button>
            </div>
          )}
        </>
      )}
      <div className="qp-footer">Powered by <strong style={{ color: accentColor }}>{brandName}</strong></div>
    </div>
  );
}
